import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { propFirms } from '@/mocks/propfirms';
import SeoHead from '@/components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.diariode1trader.com';

function CouponBadge({ coupon, discount }: { coupon: string; discount: string }) {
  return (
    <div className="flex items-center gap-1.5 bg-[#0d1f3c] border border-blue-800 rounded-full px-3 py-1">
      <i className="ri-coupon-3-fill text-blue-400 text-xs"></i>
      <span className="text-white font-bold text-xs tracking-wide">{coupon}</span>
      <span className="text-blue-300 text-xs">— {discount}</span>
    </div>
  );
}

function CouponCopyBlock({ coupon, discount }: { coupon: string; discount: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(coupon);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between bg-white/10 rounded-lg px-3 py-2 gap-2">
      <div className="text-left">
        <div className="text-white font-extrabold text-lg tracking-widest">{coupon}</div>
        <div className="text-blue-300 text-xs">{discount} na avaliação</div>
      </div>
      <button
        onClick={handleCopy}
        className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-400 text-white font-bold text-xs px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
      >
        <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
        {copied ? 'Copiado!' : 'Copiar'}
      </button>
    </div>
  );
}

const comparacaoSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Comparação de Prop Firms 2026',
    description: 'Lista comparativa das melhores prop firms para trading de futuros e forex em Portugal.',
    url: `${siteUrl}/comparacao`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'FTMO', url: 'https://ftmo.com' },
      { '@type': 'ListItem', position: 2, name: 'ThinkCapital', url: 'https://thinkcapital.com' },
      { '@type': 'ListItem', position: 3, name: 'Apex Trader Funding', url: 'https://apextraderfunding.com' },
      { '@type': 'ListItem', position: 4, name: 'E8 Markets', url: 'https://e8markets.com' },
      { '@type': 'ListItem', position: 5, name: 'The Funded Trader', url: 'https://thefundedtrader.com' },
      { '@type': 'ListItem', position: 6, name: 'My Forex Funds', url: 'https://myforexfunds.com' },
      { '@type': 'ListItem', position: 7, name: 'Topstep', url: 'https://topstep.com' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Qual é a melhor prop firm em 2026?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A FTMO é considerada a melhor prop firm em 2026 com um score de 9.4/10, graças ao seu profit split de 90%, reputação sólida e condições competitivas. Para traders de forex, a ThinkCapital (9.2/10) é uma excelente alternativa com 1 fase de avaliação.',
        },
      },
      {
        '@type': 'Question',
        name: 'O que é uma prop firm?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Uma prop firm (proprietary trading firm) é uma empresa que fornece capital a traders para operarem nos mercados financeiros. O trader passa por uma avaliação e, se aprovado, recebe uma conta financiada. Os lucros são partilhados entre o trader e a empresa, normalmente 80-90% para o trader.',
        },
      },
      {
        '@type': 'Question',
        name: 'Como funciona o drawdown nas prop firms?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O drawdown é o limite máximo de perda permitido. Existem dois tipos: daily drawdown (perda máxima num dia) e overall drawdown (perda máxima total). Se ultrapassares qualquer um destes limites, a conta é encerrada imediatamente.',
        },
      },
      {
        '@type': 'Question',
        name: 'Qual prop firm tem o melhor profit split?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A FTMO oferece até 90% de profit split, sendo uma das mais altas do mercado. A Apex Trader Funding e a E8 Markets também oferecem splits competitivos acima de 80%.',
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Comparação de Prop Firms',
        item: `${siteUrl}/comparacao`,
      },
    ],
  },
];

export default function ComparacaoPage() {
  const [expandedSet, setExpandedSet] = useState<Set<string>>(
    new Set(propFirms.map((f) => f.id))
  );

  const toggle = (id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#f0f6ff]">
      <SeoHead
        title="Comparação de Prop Firms 2026 | Diário de 1 Trader"
        description="Compara as melhores prop firms de futuros em 2026: FTMO, Topstep, Apex, The Funded Trader e E8 Markets. Análise completa de profit split, drawdown e condições."
        keywords="comparação prop firms, melhores prop firms, FTMO, Topstep, Apex Trader Funding"
        canonical="/comparacao"
        schema={comparacaoSchema}
      />
      <Navbar />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-semibold px-4 py-2 rounded-full mb-4">
            <i className="ri-bar-chart-2-line"></i>
            Comparação Completa 2026
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#0d1f3c] mb-4">Comparação de Prop Firms</h1>
          <p className="text-slate-500 max-w-2xl mx-auto">Análise detalhada e imparcial das melhores prop firms do mercado. Clique em cada card para expandir ou recolher os detalhes.</p>
        </div>

        {/* Comparison Table Header */}
        <div className="hidden md:grid grid-cols-6 gap-4 px-6 mb-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
          <div className="col-span-2">Prop Firm</div>
          <div>Profit Split</div>
          <div>Capital Máx.</div>
          <div>Taxa Mensal</div>
          <div>Score</div>
        </div>

        <div className="space-y-4">
          {propFirms.map((firm, index) => {
            const isExpanded = expandedSet.has(firm.id);
            return (
              <div key={firm.id} className="bg-white rounded-2xl border border-blue-100 overflow-hidden">
                {/* Card Header */}
                <div
                  className="flex flex-col md:flex-row md:items-center gap-4 p-6 cursor-pointer hover:bg-blue-50/40 transition-all"
                  onClick={() => toggle(firm.id)}
                >
                  <div className="flex items-center gap-4 flex-1 md:w-2/6">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 font-extrabold text-sm shrink-0">
                      #{index + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-[#0d1f3c] text-lg">{firm.name}</h3>
                        <span className="text-xs bg-blue-100 text-blue-700 font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap">{firm.badge}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className={`ri-star-fill text-xs ${i < Math.floor(firm.rating) ? 'text-yellow-400' : 'text-slate-200'}`}></i>
                        ))}
                        <span className="text-xs text-slate-400 ml-1">{firm.rating}/5</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 md:gap-0 md:contents">
                    <div className="md:flex-1 text-center">
                      <div className="text-xs text-slate-400 md:hidden mb-0.5">Profit Split</div>
                      <span className="font-bold text-green-600">{firm.profitSplit}</span>
                    </div>
                    <div className="md:flex-1 text-center">
                      <div className="text-xs text-slate-400 md:hidden mb-0.5">Capital Máx.</div>
                      <span className="font-bold text-[#0d1f3c]">{firm.maxFunding}</span>
                    </div>
                    <div className="md:flex-1 text-center">
                      <div className="text-xs text-slate-400 md:hidden mb-0.5">Taxa Mensal</div>
                      <span className="text-slate-600">{firm.monthlyFee}</span>
                    </div>
                    <div className="md:flex-1 text-center">
                      <div className="text-xs text-slate-400 md:hidden mb-0.5">Score</div>
                      <span className="text-2xl font-extrabold text-blue-600">{firm.score}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-end gap-1.5">
                      {/* Cupão destaque */}
                      <CouponBadge coupon={firm.coupon} discount={firm.couponDiscount} />
                      <a
                        href={firm.affiliateUrl}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-5 py-2.5 rounded-full whitespace-nowrap cursor-pointer transition-all"
                      >
                        Começar Agora
                      </a>
                    </div>
                    <i className={`text-slate-400 text-xl transition-transform duration-300 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                  </div>
                </div>

                {/* Expanded Details — visible for all by default */}
                {isExpanded && (
                  <div className="border-t border-blue-100 p-6 bg-blue-50/30">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Pros */}
                      <div>
                        <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2 text-sm">
                          <i className="ri-thumb-up-line"></i>Pontos Fortes
                        </h4>
                        <ul className="space-y-2">
                          {firm.pros.map((pro) => (
                            <li key={pro} className="flex items-start gap-2 text-sm text-slate-600">
                              <i className="ri-check-line text-green-500 mt-0.5 shrink-0"></i>{pro}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Cons */}
                      <div>
                        <h4 className="font-bold text-red-600 mb-3 flex items-center gap-2 text-sm">
                          <i className="ri-thumb-down-line"></i>Pontos Fracos
                        </h4>
                        <ul className="space-y-2">
                          {firm.cons.map((con) => (
                            <li key={con} className="flex items-start gap-2 text-sm text-slate-600">
                              <i className="ri-close-line text-red-400 mt-0.5 shrink-0"></i>{con}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Conditions */}
                      <div>
                        <h4 className="font-bold text-[#0d1f3c] mb-3 flex items-center gap-2 text-sm">
                          <i className="ri-file-list-3-line"></i>Condições
                        </h4>
                        <div className="space-y-3 text-sm">
                          {[
                            { label: 'Drawdown', value: firm.drawdown },
                            { label: 'Dias Mínimos', value: firm.minDays },
                            { label: 'Instrumentos', value: firm.instruments },
                          ].map(({ label, value }) => (
                            <div key={label} className="flex flex-col">
                              <span className="text-slate-400 text-xs uppercase tracking-wider mb-0.5">{label}</span>
                              <span className="font-semibold text-slate-700">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="flex flex-col justify-center items-center text-center gap-4">
                        <div className="text-4xl font-extrabold text-blue-600">
                          {firm.score}<span className="text-base text-slate-400">/10</span>
                        </div>
                        <p className="text-xs text-slate-500">Score FuturosPro baseado em reputação, condições e experiência do trader</p>
                        {/* Cupão expandido */}
                        <div className="w-full bg-[#0d1f3c] rounded-xl p-3 border border-blue-800">
                          <div className="flex items-center justify-center gap-1.5 mb-1">
                            <i className="ri-coupon-3-fill text-blue-400 text-sm"></i>
                            <span className="text-blue-300 text-xs font-bold uppercase tracking-wide">Cupão Exclusivo</span>
                          </div>
                          <CouponCopyBlock coupon={firm.coupon} discount={firm.couponDiscount} />
                        </div>
                        <a
                          href={firm.affiliateUrl}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap text-center block"
                        >
                          Começar com {firm.name}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <p className="text-center text-xs text-slate-400 mt-10">
          * Este site contém links de afiliado. Podemos receber comissões ao referenciar novos utilizadores. As análises são baseadas na nossa experiência e são independentes.
        </p>
      </div>
      <Footer />
    </div>
  );
}
